import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { MessageCircle, X, Send, User, Check, CheckCheck } from 'lucide-react';
import { AuthContext } from '../App';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export default function ChatWidget() {
  const { user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [showUserList, setShowUserList] = useState(true);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadByUser, setUnreadByUser] = useState({});
  const [previousMessageCount, setPreviousMessageCount] = useState(0);
  const [onlineUsers, setOnlineUsers] = useState({});
  const token = localStorage.getItem('token');
  const messagesEndRef = useRef(null);

  // Scroll to bottom of messages — but only when appropriate, so users can
  // scroll up and read older messages without being yanked back down.
  const messagesContainerRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const prevMsgCountRef = useRef(0);
  const prevConversationRef = useRef(null);

  const scrollToBottom = (behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    isNearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  useEffect(() => {
    const conversationChanged = prevConversationRef.current !== (selectedUser?.id || null);
    const gotNewMessages = messages.length > prevMsgCountRef.current;
    if (messages.length > 0 && (conversationChanged || (gotNewMessages && isNearBottomRef.current))) {
      scrollToBottom(conversationChanged ? 'auto' : 'smooth');
    }
    prevMsgCountRef.current = messages.length;
    prevConversationRef.current = selectedUser?.id || null;
  }, [messages, selectedUser]);

  // Fetch online users
  const fetchOnlineUsers = async () => {
    try {
      const response = await axios.get(`${API}/api/users/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const onlineMap = {};
      response.data.forEach(u => {
        onlineMap[u.user_id] = u.is_online;
      });
      setOnlineUsers(onlineMap);
    } catch (error) {
      console.error('Failed to fetch online users');
    }
  };

  // Send heartbeat to track presence
  const sendHeartbeat = async () => {
    try {
      await axios.post(`${API}/api/users/heartbeat`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Failed to send heartbeat');
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchUnreadCount();
    fetchOnlineUsers();
    sendHeartbeat();
    
    // Poll for new messages every 5 seconds
    const interval = setInterval(() => {
      if (selectedUser) {
        fetchConversation(selectedUser.id, true);
      }
      fetchUnreadCount();
      fetchOnlineUsers();
    }, 5000);
    
    // Send heartbeat every 2 minutes
    const heartbeatInterval = setInterval(sendHeartbeat, 120000);
    
    // Mark offline when leaving
    return () => {
      clearInterval(interval);
      clearInterval(heartbeatInterval);
      axios.post(`${API}/api/users/offline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    };
  }, [selectedUser]);

  const fetchUsers = async () => {
    try {
      // Use chat-specific endpoint that allows any authenticated user
      const response = await axios.get(`${API}/api/users/chat`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.filter(u => u.id !== user?.id));
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/api/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(response.data.count);
      
      // Also fetch unread counts per user
      const unreadByUserResponse = await axios.get(`${API}/api/messages/unread-by-user`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('[CHAT DEBUG] Unread by user data:', unreadByUserResponse.data);
      setUnreadByUser(unreadByUserResponse.data);
    } catch (error) {
      console.error('Failed to fetch unread count', error);
    }
  };

  const fetchConversation = async (userId, isPolling = false) => {
    try {
      // Use the new conversation endpoint
      const response = await axios.get(`${API}/api/messages/conversation/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const conversation = response.data;
      
      // Check for new messages (only during polling, not initial load)
      if (isPolling && conversation.length > previousMessageCount) {
        // New messages detected - count updated below
      }
      
      setMessages(conversation);
      setPreviousMessageCount(conversation.length);
      
      // Mark unread messages as seen
      const unreadMessages = conversation.filter(m => m.sender_id === userId && !m.is_read);
      for (const msg of unreadMessages) {
        try {
          await axios.patch(`${API}/api/messages/${msg.id}/seen`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error('Failed to mark message as seen:', error);
        }
      }
      
      if (unreadMessages.length > 0) {
        fetchUnreadCount();
      }
    } catch (error) {
      console.error('Failed to load conversation');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;

    try {
      await axios.post(`${API}/api/messages`, {
        recipient_id: selectedUser.id,
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNewMessage('');
      fetchConversation(selectedUser.id);
    } catch (error) {
      console.error('Failed to send message:', error.response?.data || error.message);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ') : (typeof detail === 'string' ? detail : 'Failed to send message');
      toast.error(msg);
    }
  };

  const openChat = (selectedUser) => {
    setSelectedUser(selectedUser);
    setShowUserList(false);
    setPreviousMessageCount(0); // Reset counter when opening chat
    fetchConversation(selectedUser.id);
  };

  const closeChat = () => {
    setSelectedUser(null);
    setShowUserList(true);
    setMessages([]);
    setPreviousMessageCount(0);
  };

  return (
    <>
      {/* Floating Chat Button */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        {!isOpen && (
          <Button
            onClick={() => setIsOpen(true)}
            data-testid="chat-widget-toggle-btn"
            className="h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg bg-blue-600 hover:bg-blue-700 relative"
          >
            <MessageCircle className="w-5 h-5 sm:w-6 sm:h-6" />
            {unreadCount > 0 && (
              <Badge className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs">
                {unreadCount}
              </Badge>
            )}
          </Button>
        )}

        {/* Chat Window */}
        {isOpen && (
          <div className="bg-white rounded-lg shadow-2xl w-[calc(100vw-2rem)] sm:w-96 h-[70vh] sm:h-[500px] max-h-[600px] flex flex-col border border-slate-200">
            {/* Header */}
            <div className="bg-blue-600 text-white p-4 rounded-t-lg flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                <div>
                  <h3 className="font-semibold">
                    {selectedUser ? selectedUser.name : 'Messages'}
                  </h3>
                  {/* Show online status when user is selected */}
                  {selectedUser && (
                    <span className={`text-xs ${onlineUsers[selectedUser.id] ? 'text-green-300' : 'text-blue-200'}`}>
                      {onlineUsers[selectedUser.id] ? '● Online' : '○ Offline'}
                    </span>
                  )}
                </div>
                {unreadCount > 0 && !selectedUser && (
                  <Badge className="bg-red-500 text-white">
                    {unreadCount}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedUser && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={closeChat}
                    className="text-white hover:bg-blue-700 h-8 w-8 p-0"
                  >
                    ←
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                  className="text-white hover:bg-blue-700 h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* User List */}
            {showUserList && (
              <div className="flex-1 overflow-y-auto p-2">
                <p className="text-xs text-slate-500 px-2 py-2">Select a user to chat with:</p>
                {users
                  .sort((a, b) => {
                    // Sort: users with unread messages first, then online users
                    const aHasUnread = unreadByUser[a.id]?.count > 0;
                    const bHasUnread = unreadByUser[b.id]?.count > 0;
                    if (aHasUnread && !bHasUnread) return -1;
                    if (!aHasUnread && bHasUnread) return 1;
                    // Then sort by online status
                    const aOnline = onlineUsers[a.id];
                    const bOnline = onlineUsers[b.id];
                    if (aOnline && !bOnline) return -1;
                    if (!aOnline && bOnline) return 1;
                    return 0;
                  })
                  .map(u => (
                    <div
                      key={u.id}
                      onClick={() => openChat(u)}
                      data-testid={`chat-user-${u.id}`}
                      className="flex items-center gap-3 p-3 hover:bg-slate-100 rounded-lg cursor-pointer"
                    >
                      {/* Red badge with unread count */}
                      {unreadByUser[u.id]?.count > 0 && (
                        <div className="bg-red-500 text-white rounded-full h-6 w-6 flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {unreadByUser[u.id].count}
                        </div>
                      )}
                      
                      <div className="bg-blue-100 rounded-full p-2 flex-shrink-0 relative">
                        <User className="w-5 h-5 text-blue-600" />
                        {/* Online/Offline indicator dot */}
                        <span 
                          className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white ${
                            onlineUsers[u.id] ? 'bg-green-500' : 'bg-gray-400'
                          }`}
                          title={onlineUsers[u.id] ? 'Online' : 'Offline'}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{u.name}</p>
                          {onlineUsers[u.id] && (
                            <span className="text-xs text-green-600 font-medium">Online</span>
                          )}
                        </div>
                        <p className="text-xs text-slate-500 capitalize">{u.role}</p>
                      </div>
                    </div>
                  ))}
              </div>
            )}

            {/* Chat Messages */}
            {selectedUser && !showUserList && (
              <>
                <div ref={messagesContainerRef} onScroll={handleMessagesScroll} data-testid="chat-messages-container" className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
                  {messages.length === 0 ? (
                    <p className="text-center text-slate-500 text-sm py-8">No messages yet. Start chatting!</p>
                  ) : (
                    messages.map(msg => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[75%] rounded-lg px-4 py-2 ${
                            msg.sender_id === user?.id
                              ? 'bg-blue-600 text-white'
                              : 'bg-white border border-slate-200 text-slate-900'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <div className="flex items-center gap-1 mt-1">
                            <p className={`text-xs ${
                              msg.sender_id === user?.id ? 'text-blue-100' : 'text-slate-500'
                            }`}>
                              {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </p>
                            {/* Show seen indicator for sent messages */}
                            {msg.sender_id === user?.id && msg.seen_at && (
                              <CheckCheck className="w-3 h-3 text-blue-200" title="Seen" />
                            )}
                            {msg.sender_id === user?.id && !msg.seen_at && msg.is_read && (
                              <Check className="w-3 h-3 text-blue-200" title="Delivered" />
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Message Input */}
                <form onSubmit={handleSendMessage} className="p-3 border-t bg-white">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type a message..."
                      data-testid="chat-message-input"
                      className="flex-1"
                      autoFocus
                    />
                    <Button type="submit" size="sm" data-testid="chat-send-btn" className="bg-blue-600 hover:bg-blue-700">
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}
      </div>
    </>
  );
}
